/**
 * Timer race cancellation (SPEC §7.3: "Cancel all pending timers on exit/advance").
 *
 * The mockup's bug class was leaked setTimeout handles that fired after the
 * session screen had been swapped out — phantom state changes / stuck sheets.
 * `later()` and `clearTimers()` together provide a per-component session of
 * cancellable timers. Always call `clearTimers()` in the cleanup of a useEffect
 * tied to a screen that schedules anything.
 */
type TimerHandle = ReturnType<typeof setTimeout>

export interface TimerSession {
  later(fn: () => void, ms: number): TimerHandle
  clearAll(): void
}

export function makeTimerSession(): TimerSession {
  const handles = new Set<TimerHandle>()
  return {
    later(fn, ms) {
      const h = setTimeout(() => {
        handles.delete(h)
        fn()
      }, ms)
      handles.add(h)
      return h
    },
    clearAll() {
      for (const h of handles) clearTimeout(h)
      handles.clear()
    },
  }
}
